import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import {
  ScheduleRequestSource,
  Provider as ProviderV2,
} from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Provider as ProviderV3 } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs, { ConfigType } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import env from '@frontend/env';
import { ExtendedAppointment } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const DATE_TIME_DISPLAY_FORMAT = 'MM/DD/YYYY hh:mm A';

export const isValidDate = (date: dayjs.Dayjs): boolean => {
  // Check if the date is valid and not before the year 1900
  return date.isValid() && date.year() >= 1900;
};

// Define an interface for the input options
export interface FormatDateTimeOptions {
  fromTimezone?: string; // The source timezone (e.g., 'America/New_York')
  toTimezone?: string; // The target timezone (e.g., 'Asia/Tokyo')
  inputFormat?: string; // The format of the input date-time (optional)
  outputFormat?: string; // The desired output format (optional)
}

export function formatDateTime(inputDateTime: ConfigType, options?: FormatDateTimeOptions): string {
  const { fromTimezone, toTimezone, inputFormat, outputFormat = DATE_TIME_DISPLAY_FORMAT } = options || {};

  if (!inputDateTime) {
    return '';
  }

  // remove Z from the input date-time string to avoid parsing issues (as for that case, it's already in UTC)
  if (typeof inputDateTime === 'string' && inputDateTime.endsWith('Z')) {
    inputDateTime = inputDateTime.slice(0, -1);
  }

  const date = inputFormat ? dayjs(inputDateTime, inputFormat) : dayjs(inputDateTime);

  if (!isValidDate(date)) {
    return '';
  }

  // Assuming we no need to convert to timezone if either of from or to timezone is not provided
  if (!fromTimezone || !toTimezone) {
    return date.format(outputFormat);
  }

  const convertedDate = date.tz(fromTimezone, true).tz(toTimezone);

  return convertedDate.format(outputFormat);
}

const getAppointmentDate = (event: CalendarEvent, timezone: string) => {
  return formatDateTime(`${event.startDate} ${event.startTime}`, {
    fromTimezone: 'UTC',
    toTimezone: timezone,
  });
};

interface CalendarEventDateFields {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
}
export const convertEventTimesFromUTC = <T extends CalendarEventDateFields>(event: T, locationTimeZone: string) => {
  const startDateTime = formatDateTime(`${event.startDate} ${event.startTime}`, {
    fromTimezone: 'UTC',
    toTimezone: locationTimeZone,
  });
  const endDateTime = formatDateTime(`${event.endDate} ${event.endTime}`, {
    fromTimezone: 'UTC',
    toTimezone: locationTimeZone,
  });

  return {
    ...event,
    startDate: formatDate(startDateTime, 'YYYY-MM-DD'),
    startTime: formatDate(startDateTime, 'HH:mm:ss'),
    endDate: formatDate(endDateTime, 'YYYY-MM-DD'),
    endTime: formatDate(endDateTime, 'HH:mm:ss'),
  };
};

export const convertScheduleRequestsTimesFromUTC = (
  scheduleRequest: ScheduleRequest,
  locationTimeZone: string
): ScheduleRequest => {
  const convertedSlots = (scheduleRequest.requestedOpenings || []).map((opening) => ({
    ...opening,
    dateTime: formatDateTime(opening.dateTime, {
      fromTimezone: 'UTC',
      toTimezone: locationTimeZone,
    }),
  }));

  return {
    ...scheduleRequest,
    requestedOpenings: convertedSlots,
  };
};

const checkIsOverlappingEvent = (
  event: Partial<Record<'startDate' | 'endDate' | 'startTime' | 'endTime', string>>
): boolean => {
  const { startDate, endDate, startTime, endTime } = event;
  return !!(startDate && endDate && startTime && endTime && startDate !== endDate && endTime !== '00:00:00');
};

export const convertV3CalendarEventToV2Appointment = (
  event: CalendarEvent,
  locationTimezoneMap?: SchedulerV3.Helpers.LocationTimezoneMap
): ExtendedAppointment => {
  const locationId = event.locationId ?? '';

  const locationTimezone = locationTimezoneMap?.[locationId]?.timezone;
  const convertedEvent = locationTimezone ? convertEventTimesFromUTC(event, locationTimezone) : event;
  const isOverlapping = checkIsOverlappingEvent(convertedEvent);
  const operatoryName =
    event.operatoryDetails?.displayName || event.operatoryDetails?.externalName || event.details?.opNa || '';

  const appointment = {
    id: event.id ?? '',
    externalId: event.details?.pReId ?? '',
    locationIds: locationId ? [locationId] : [], // Revisit
    locationId: locationId,
    // NOTE startDate: V2 format is YYYY-MM-DDTHH:mm:ssZ. When a timezone is present we're returning format MM/DD/YYYY hh:mm A
    start: locationTimezoneMap?.[locationId]
      ? getAppointmentDate(event, locationTimezoneMap[locationId]?.timezone)
      : dayjs(`${event.startDate} ${event.startTime}`).toString(),
    // type: Previous code was using eventType as a fallback to populate the type field. EventType is not the appointment type. An appointment is created with the eventType set to 'APPOINTMENT'. type: event.details?.rTyNa === appointment type
    type: event.details?.rTyNa ?? '',
    appointmentTypeId: event.referenceTypeId ?? '',
    duration: event.details?.dur,
    externalStatus: event.attendeeStatus,
    // Set the practitioner name to an empty string, if the provider is unassigned
    // TODO: Update when event.details?.orNa is empty from API
    practitionerName: event.organizerId === '' ? '' : event.details?.orNa,
    providerIds: event.organizerId ? [event.organizerId] : [],
    operatory: operatoryName,
    person: {
      firstName: event.attendeeDetails?.firstName,
      lastName: event.attendeeDetails?.lastName,
      preferredName: event.attendeeDetails?.preferredName,
      personId: event.attendeeDetails?.personId,
      gender: event.attendeeDetails?.gender,
      status: event.attendeeDetails?.status,
      birthDate: event.attendeeDetails?.birthdate,
      householdId: event.attendeeDetails?.householdId,
      mobilePhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_MOBILE)
        ?.destination,
      homePhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_HOME)
        ?.destination,
      workPhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_WORK)
        ?.destination,
      sourceId: event.attendeeDetails?.sourceId || '',
    },
    sourceTenantId: event.sourceTenantId ?? '',
    createdBySourceId: event.sourceTenantId,
    workstationIds: event.operatoryId ? [event.operatoryId] : [],
    isNonIntegratedSource: !event.isIntegrated,
    statusOfficeView: event.attendeeStatus,
    referenceId: event.referenceId ?? '',
    isOverlapping,
    displayOptions: {
      primaryColor: event.displayOptions?.primaryColor,
      backgroundColor: event.displayOptions?.backgroundColor,
      textColor: event.displayOptions?.textColor,
    },
  };

  return appointment;
};

export const convertV3CalendarEventsToV2Appointments = (
  events: CalendarEvent[],
  locationTimezoneMap?: SchedulerV3.Helpers.LocationTimezoneMap
): ExtendedAppointment[] => events.map((event) => convertV3CalendarEventToV2Appointment(event, locationTimezoneMap));

export const convertV3ProviderToV2Provider = (practitioners: ProviderV3[]): ProviderV2[] =>
  practitioners.map((practitioner) => ({
    id: practitioner.id,
    firstName: practitioner.firstName,
    lastName: practitioner.lastName,
    publicDisplayName: practitioner.displayName || '',
    locationId: practitioner.locationId,
    resourceName: practitioner.externalName,
    externalId: practitioner.externalId,
    publicDisplayImage: practitioner?.profile?.picUrl,
    createdAt: practitioner.createdAt,
    updatedAt: practitioner.updatedAt,
    publicDisplayDescription: practitioner.profile?.bio || '',
    sourceId: practitioner.sourceTenantId,
    isActive: !practitioner.isArchived,
  }));

export const getBookingSiteUrl = (locationId: string, source = ScheduleRequestSource.WEBSITE): string => {
  return `${env.APP_BOOK_BASE_URL_NEW}/${locationId}/request-appointment?${new URLSearchParams({ source }).toString()}`;
};
